import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { 
  AsmtRunnerSend, 
  AsmtRunnerReceive, 
  ExtensionStatus, 
  AssessmentLockState 
} from '../../app/constants/assessment-lock-signals';
import { AuthService } from '../api/auth.service';
import { RoutesService } from '../api/routes.service';

/**
 * Assessment Lock Service
 * 
 * Manages communication with the Vretta Assessment Lock Chrome Extension.
 * Handles lockdown state, ping/pong heartbeat, and automatic error recovery.
 * 
 * Logged Events (for monitoring and debugging):
 * - ASSESSMENT_LOCK_CONTEXT_INVALIDATED: Extension context became invalid (e.g., extension updated/reloaded)
 * - ASSESSMENT_LOCK_EXTENSION_TIMEOUT: Extension stopped responding to pings during active lock
 * - ASSESSMENT_LOCK_UNEXPECTED_DEACTIVATION: Lock deactivated unexpectedly while app expected it active
 * - ASSESSMENT_LOCK_ACTIVATION_FAILED: Failed to activate assessment lock
 * - ASSESSMENT_LOCK_DEACTIVATION_FAILED: Failed to deactivate assessment lock
 * 
 * Recovery Behavior:
 * - If extension becomes unavailable during active lock, page automatically reloads
 * - If context is invalidated during active lock, page automatically reloads
 */
@Injectable({
  providedIn: 'root'
})
export class AssessmentLockService {
  private readonly PING_INTERVAL = 30000; // 30 seconds
  private readonly PONG_TIMEOUT = 5000;   // 5 seconds

  // State management
  private stateSubject = new BehaviorSubject<AssessmentLockState>({
    isLockdown: false,
    triggerUrl: null,
    triggerTabId: null,
    previousTabIds: [],
    extensionStatus: ExtensionStatus.CHECKING
  });

  public state$: Observable<AssessmentLockState> = this.stateSubject.asObservable();

  // Internal tracking
  private pingIntervalId: number | null = null;
  private pongTimeoutId: number | null = null;
  private isFirstPongReceived = false;
  private isDisallowed = false;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private isInitialized = false;
  private supervisorPassword:string;
  public isActive = false;

  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    private ngZone: NgZone
  ) {
    // Don't auto-initialize - let components control when to start
  }

  /**
   * Initialize the service and start monitoring for extension
   * Call this when you need to start checking for the extension
   */
  init(): void {
    if (this.isInitialized) {
      return;
    }
    this.initializeMessageListener();
    this.startPingInterval();
    this.isInitialized = true;
  }

  ensureChrombookAllow(isNonChrombookAllowed: boolean): void {
    if (!this.isChromebook() && !this.isFirstPongReceived) {
      this.isDisallowed = true
    }
  }

  setSupervisorPassword(supervisorPassword: string): void {
    this.supervisorPassword = supervisorPassword;
  }

  /**
   * Get current state snapshot
   */
  get currentState(): AssessmentLockState {
    return this.stateSubject.value;
  }

  /**
   * Check if extension is available and responding
   */
  get isExtensionAvailable(): boolean {
    return this.isFirstPongReceived && !this.isDisallowed;
    // this.currentState.extensionStatus === ExtensionStatus.CONNECTED;
  }

  /**
   * Get tab restoration count (security monitoring)
   */
  get tabRestorationCount(): number {
    return this.currentState.previousTabIds?.length || 0;
  }

  /**
   * Check if suspicious activity detected (multiple tab closures)
   */
  get isSuspiciousActivity(): boolean {
    return this.tabRestorationCount >= 3;
  }

  /**
   * Get current trigger tab ID
   */
  get currentTriggerTabId(): number | null {
    return this.currentState.triggerTabId;
  }

  /**
   * Detect if user is on a Chromebook
   */
  isChromebook(): boolean {
    const userAgent = navigator.userAgent;
    return userAgent.includes('CrOS') || userAgent.includes('Chrome OS');
  }

  /**
   * Log assessment lock issues
   */
  private logAssessmentLockEvent(slug: string, details: any): void {
    if (!this.auth.userIsStudent()) {
      return;
    }
    
    this.auth.apiCreate(this.routes.LOG, {
      slug: slug,
      data: {
        uid: this.auth.getUid(),
        timestamp: new Date().toISOString(),
        isActive: this.isActive,
        extensionStatus: this.currentState.extensionStatus,
        isLockdown: this.currentState.isLockdown,
        ...details
      }
    }).catch(err => {
      console.error('Failed to log assessment lock event:', err);
    });
  }

  /**
   * Initialize message listener for extension signals
   */
  private initializeMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      // Security: Only accept messages from same origin
      if (event.origin !== window.location.origin || event.source !== window) {
        return;
      }

      const message = event.data;

      this.ngZone.run(() => {
        switch (message.type) {
          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_ACTIVATED:
            this.updateState({
              isLockdown: true,
              triggerUrl: message.state?.triggerUrl || window.location.href,
              triggerTabId: message.state?.triggerTabId || null,
              previousTabIds: []  // Reset on activation
            });
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_DEACTIVATED:
            this.updateState({
              isLockdown: false,
              triggerUrl: null,
              triggerTabId: null,
              previousTabIds: []  // Reset on deactivation
            });
            this.isActive = false;
            this.onExitLock();
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_PONG:
            this.handlePong(message);
            break;

          case AsmtRunnerReceive.VRETTA_EXTENSION_CONTEXT_INVALIDATED:
            console.error('Extension context invalidated:', message.reason);
            this.handleContextInvalidated();
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_ACTIVATION_FAILED:
            console.error('Activation failed:', message.error);
            this.logAssessmentLockEvent('ASSESSMENT_LOCK_ACTIVATION_FAILED', {
              error: message.error,
              reason: message.reason || 'Unknown',
              triggerUrl: window.location.href
            });
            break;

          case AsmtRunnerReceive.VRETTA_ASSESSMENT_LOCK_DEACTIVATION_FAILED:
            console.error('Deactivation failed:', message.error);
            this.logAssessmentLockEvent('ASSESSMENT_LOCK_DEACTIVATION_FAILED', {
              error: message.error,
              reason: message.reason || 'Unknown',
              triggerUrl: this.currentState.triggerUrl
            });
            break;
        }
      });
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Send ping to extension
   */
  private sendPing(): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_ASSESSMENT_LOCK_PING
    }, '*');

    // Set timeout for no response
    this.pongTimeoutId = window.setTimeout(() => {
      console.warn('No pong received from extension within 5 seconds');
      
      // If lock is active and extension stops responding, log and reload
      if (this.isActive) {
        this.logAssessmentLockEvent('ASSESSMENT_LOCK_EXTENSION_TIMEOUT', {
          reason: 'Extension stopped responding to ping during active lock',
          wasFirstPongReceived: this.isFirstPongReceived,
          triggerUrl: this.currentState.triggerUrl,
          triggerTabId: this.currentState.triggerTabId
        });
        
        console.error('Extension stopped responding during active lock - reloading page');
        window.location.reload();
        return;
      }
      
      this.ngZone.run(() => {
        this.updateState({
          extensionStatus: this.isFirstPongReceived 
            ? ExtensionStatus.NOT_RESPONDING 
            : ExtensionStatus.NOT_INSTALLED
        });
      });
      this.pongTimeoutId = null;
    }, this.PONG_TIMEOUT);
  }

  /**
   * Handle pong response
   */
  private handlePong(message: any): void {
    // Clear timeout
    if (this.pongTimeoutId !== null) {
      clearTimeout(this.pongTimeoutId);
      this.pongTimeoutId = null;
    }

    // Mark first pong received
    if (!this.isFirstPongReceived) {
      this.isFirstPongReceived = true;
      setTimeout(() => {
        console.log('isFirstPongReceived')
      }, 1000)
    }

    const isLockdown = (message.isLockdown || false); 
    
    // Detect unexpected lock deactivation
    if (this.isActive && !isLockdown){
      console.warn('Assessment lock unexpectedly deactivated');
      this.logAssessmentLockEvent('ASSESSMENT_LOCK_UNEXPECTED_DEACTIVATION', {
        reason: 'Lock was active but pong indicates it is no longer active',
        previousTabIds: message.previousTabIds || [],
        triggerTabId: message.triggerTabId || null
      });
      this.onExitLock();
    }
    
    this.isActive = isLockdown

    // Update state with all tracking info
    this.updateState({
      isLockdown: isLockdown,
      triggerTabId: message.triggerTabId || null,
      previousTabIds: message.previousTabIds || [],
      extensionStatus: ExtensionStatus.CONNECTED
    });
  }

  /**
   * Handle context invalidation
   */
  private handleContextInvalidated(): void {
    console.error('Extension context invalidated');
    
    this.updateState({
      extensionStatus: ExtensionStatus.CONTEXT_INVALIDATED
    });
    
    // Log the context invalidation, especially critical if lock is active
    this.logAssessmentLockEvent('ASSESSMENT_LOCK_CONTEXT_INVALIDATED', {
      reason: 'Extension context was invalidated',
      triggerUrl: this.currentState.triggerUrl,
      triggerTabId: this.currentState.triggerTabId,
      willReload: this.isActive
    });
    
    this.stopPingInterval();
    
    // Reload page if lock is active to restore proper state
    if (this.isActive) {
      console.error('Extension context invalidated during active lock - reloading page');
      // Small delay to ensure log is sent
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  /**
   * Start ping interval
   */
  private startPingInterval(): void {
    // Send initial ping
    this.sendPing();

    // Send ping every 30 seconds
    this.pingIntervalId = window.setInterval(() => {
      this.sendPing();
    }, this.PING_INTERVAL);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }

    if (this.pongTimeoutId !== null) {
      clearTimeout(this.pongTimeoutId);
      this.pongTimeoutId = null;
    }
  }

  /**
   * Update state (partial update)
   */
  private updateState(partial: Partial<AssessmentLockState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({
      ...currentState,
      ...partial
    });
  }

  /**
   * Activate assessment lock with optional supervisor password
   */
  
  activateLock(callback?: () => any): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_ACTIVATE_ASSESSMENT_LOCK,
      url: window.location.href,
      supervisorPassword: this.supervisorPassword  // Password for unlock validation
    }, '*');
    this.isActive = true;
    this.onExitLock = () => callback(); // todo: should use a sub
  }

  onExitLock: () => any;

  /**
   * Deactivate assessment lock
   * Note: Extension will prompt for supervisor password if EXIT_PASSWORD_PROMPT is enabled
   */
  deactivateLock(): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_DEACTIVATE_ASSESSMENT_LOCK
    }, '*');
    // this.isActive = false; // ?
  }

  deactivateLockForced(): void {
    window.postMessage({
      type: AsmtRunnerSend.VRETTA_DEACTIVATE_ASSESSMENT_LOCK,
      isSkipPasswordPrompt: true,
      supervisorPassword: this.supervisorPassword
    }, '*');
    // this.isActive = false; // ?
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.stopPingInterval();
    
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
  }
}
